<template>
  <RootPageLayout>
    <template #header>
      <div class="flex w-full justify-between">
        <PageTitle> Reporting </PageTitle>

        <ReportsTimePeriodSelect v-model="timePeriod" />
      </div>
    </template>

    <QueryLoading v-if="isSummaryLoading" />
    <QueryError
      v-else-if="isSummaryError && summaryData == null"
      class="mb-4"
    >
      Failed to retrieve summary report
    </QueryError>
    <div v-else class="mb-4 flex w-full justify-end">
      <div class="grid w-full grid-cols-2 gap-2">
        <template
          v-for="summaryItem in formattedSummary"
          :key="summaryItem.label"
        >
          <div
            class="flex flex-col items-center rounded-lg bg-background p-3"
          >
            <p
              class="mb-2 line-clamp-1 text-xs font-medium text-background-fg/70"
            >
              {{ summaryItem.label }}
            </p>
            <div
              class="mb-2 line-clamp-1 text-2xl font-bold text-primary"
            >
              {{ summaryItem.value }}
            </div>
            <PercentageChangeChip
              v-if="
                summaryItem.percentageChange !== undefined
              "
              :percentage-change="
                summaryItem.percentageChange
              "
            />
            <p class="mt-1 line-clamp-1 text-xs">
              {{ summaryItem.comparisonDescription }}
            </p>
          </div>
        </template>
      </div>
    </div>
    <div class="mb-4 rounded-lg bg-background p-3">
      <TransactionReportGraph :time-period="timePeriod" />
    </div>
    <div class="rounded-lg bg-background p-3">
      <TransactionReportBarChart
        :time-period="timePeriod"
      />
    </div>
  </RootPageLayout>
</template>

<script setup lang="ts">
import type { TransactionReportPeriod } from "@/services/reports/retrieveTransactionReport"
import { formatToPercentage } from "@/utils/formatToPercentage"
import { useSummaryReport } from "@/services/reports/retrieveSummaryReport"

const timePeriod = ref<TransactionReportPeriod>("Today")

const { formatToMerchantCurrencyString } = useCurrency()

const safeDiv = (a: number, b: number) => {
  if (a === 0 && b === 0) return 0

  return a / b
}
const {
  data: summaryData,
  isError: isSummaryError,
  isLoading: isSummaryLoading,
} = useSummaryReport(computed(() => timePeriod.value))

type FormattedSummaryReport = Array<{
  label:
    | "Revenue"
    | "Transactions (sales)"
    | "Average Spend"
    | "Percentage Card"
  value: number | string
  percentageChange?: number
  comparisonDescription?: string
}>

const formattedSummary = computed<FormattedSummaryReport>(
  () => {
    if (!summaryData.value) {
      return [
        { label: "Revenue", value: "-" },
        { label: "Transactions (sales)", value: "-" },
        { label: "Average Spend", value: "-" },
        { label: "Percentage Card", value: "-" },
      ]
    }

    const averageSpend = safeDiv(
      summaryData.value.revenue.total.value,
      summaryData.value.orderCount.total.value
    )
    const averageSpendComparisonValue = safeDiv(
      summaryData.value.revenue.total.comparisonValue,
      summaryData.value.orderCount.total.comparisonValue
    )
    const averageSpendPercentageChange =
      safeDiv(
        averageSpend - averageSpendComparisonValue,
        averageSpendComparisonValue
      ) * 100

    const percentageCard =
      safeDiv(
        summaryData.value.revenue.card.value,
        summaryData.value.revenue.total.value
      ) * 100
    const percentageCardComparisonValue =
      safeDiv(
        summaryData.value.revenue.card.comparisonValue,
        summaryData.value.revenue.total.comparisonValue
      ) * 100
    const percentageCardPercentageChange =
      safeDiv(
        percentageCard - percentageCardComparisonValue,
        percentageCardComparisonValue
      ) * 100

    return [
      {
        label: "Revenue",
        value: formatToMerchantCurrencyString(
          summaryData.value.revenue.total.value,
          { compact: true }
        ),
        percentageChange:
          summaryData.value.revenue.total
            .percentageChange ?? Infinity,
        comparisonDescription:
          summaryData.value.comparisonDescription,
      },
      {
        label: "Transactions (sales)",
        value: summaryData.value.orderCount.total.value,
        percentageChange:
          summaryData.value.orderCount.total
            .percentageChange ?? Infinity,
        comparisonDescription:
          summaryData.value.comparisonDescription,
      },
      {
        label: "Average Spend",
        value: formatToMerchantCurrencyString(
          averageSpend,
          {
            compact: true,
          }
        ),
        percentageChange: averageSpendPercentageChange,
        comparisonDescription:
          summaryData.value.comparisonDescription,
      },
      {
        label: "Percentage Card",
        value: formatToPercentage(percentageCard),
        percentageChange: percentageCardPercentageChange,
        comparisonDescription:
          summaryData.value.comparisonDescription,
      },
    ]
  }
)
</script>
